/**
 * Builds one batched mesh per system from a bundle, with a stable draw slot
 * per part, and exposes the per-part operations the client's render bridge
 * needs: visibility, colour, level swap.
 */
import { BatchedMesh, BufferAttribute, BufferGeometry, Color, Object3D, type Material } from "three";
import { partArrays, type LevelRange, type Manifest, type PartArrays, type PartRecord, type SystemRecord } from "./format.js";
import { StableBatchedMesh } from "./stable-batched-mesh.js";

export interface BuildOptions {
  /** The level to load into the batches now. */
  level: string;
  /** Systems to build; default every system in the manifest. */
  systems?: string[];
  materialFor: (system: SystemRecord) => Material;
  /** `false` builds plain `BatchedMesh`es (the flicker A/B in the harness). Default true. */
  stable?: boolean;
  /** Reserve capacity for these levels so `addLevel` can add them later. Default: `[level]`. */
  reserveLevels?: string[];
  /** Tint each instance with its system colour. Default true. */
  colourInstances?: boolean;
}

export interface AtlasPart {
  record: PartRecord;
  system: string;
  mesh: BatchedMesh;
  instanceId: number;
  geometryIds: Map<string, number>;
  currentLevel: string;
}

export interface AtlasStats {
  systems: number;
  instances: number;
  triangles: number;
  culled: number;
}

export class BatchedAtlas {
  readonly root = new Object3D();
  readonly meshes = new Map<string, BatchedMesh>();
  readonly parts = new Map<string, AtlasPart>();
  private readonly colour = new Color();

  constructor(readonly manifest: Manifest) {
    this.root.name = manifest.id;
  }

  setVisible(partId: string, visible: boolean): void {
    const part = this.parts.get(partId);
    if (part) part.mesh.setVisibleAt(part.instanceId, visible);
  }

  setColour(partId: string, colour: string | number): void {
    const part = this.parts.get(partId);
    if (part) part.mesh.setColorAt(part.instanceId, this.colour.set(colour));
  }

  /** Add a level's geometry for a part (capacity must have been reserved). */
  addLevel(partId: string, level: string, arrays: PartArrays): void {
    const part = this.parts.get(partId);
    if (!part || part.geometryIds.has(level)) return;
    part.geometryIds.set(level, part.mesh.addGeometry(geometryFrom(arrays)));
  }

  /** Swap a part to a loaded level. Returns false if the level is not loaded. */
  setLevel(partId: string, level: string): boolean {
    const part = this.parts.get(partId);
    const geometryId = part?.geometryIds.get(level);
    if (!part || geometryId === undefined) return false;
    if (part.currentLevel !== level) {
      part.mesh.setGeometryIdAt(part.instanceId, geometryId);
      part.currentLevel = level;
    }
    return true;
  }

  stats(): AtlasStats {
    let instances = 0;
    let triangles = 0;
    let culled = 0;
    for (const part of this.parts.values()) {
      instances += 1;
      const range = part.record.levels[part.currentLevel];
      if (range && part.mesh.getVisibleAt(part.instanceId)) triangles += range.tris;
    }
    for (const mesh of this.meshes.values()) {
      if (mesh instanceof StableBatchedMesh) culled += mesh.lastCulled;
    }
    return { systems: this.meshes.size, instances, triangles, culled };
  }
}

export function geometryFrom(arrays: PartArrays): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(arrays.positions, 3));
  geometry.setAttribute("normal", new BufferAttribute(arrays.normals, 3, true));
  geometry.setIndex(new BufferAttribute(arrays.indices, 1));
  return geometry;
}

/**
 * Build the batches. Parts are added in manifest order within a system, so
 * instance i is always slot i (the stable-slot invariant); systems draw
 * outer-to-inner through `renderOrder = layer`.
 */
export function buildBatchedAtlas(manifest: Manifest, chunks: Map<number, ArrayBuffer>, opts: BuildOptions): BatchedAtlas {
  const atlas = new BatchedAtlas(manifest);
  const wanted = new Set(opts.systems ?? manifest.systems.map((s) => s.id));
  const reserve = opts.reserveLevels ?? [opts.level];
  const stable = opts.stable ?? true;
  const colourInstances = opts.colourInstances ?? true;
  const systems = new Map(manifest.systems.map((s) => [s.id, s]));

  const bySystem = new Map<string, PartRecord[]>();
  for (const part of manifest.parts) {
    if (!wanted.has(part.system) || !part.levels[opts.level]) continue;
    let list = bySystem.get(part.system);
    if (!list) bySystem.set(part.system, (list = []));
    list.push(part);
  }

  for (const [systemId, parts] of bySystem) {
    const system = systems.get(systemId);
    if (!system) continue;
    let vertices = 0;
    let indices = 0;
    for (const part of parts) {
      for (const level of reserve) {
        const range = part.levels[level];
        if (range) {
          vertices += range.v;
          indices += range.i;
        }
      }
    }
    const material = opts.materialFor(system);
    const mesh = (stable
      ? new StableBatchedMesh(parts.length, vertices, indices, material)
      : new BatchedMesh(parts.length, vertices, indices, material)) as BatchedMesh;
    mesh.name = `atlas:${systemId}`;
    mesh.renderOrder = system.layer;
    for (const part of parts) {
      const range = part.levels[opts.level] as LevelRange;
      const buffer = chunks.get(range.chunk);
      if (!buffer) throw new Error(`chunk ${range.chunk} for ${part.id} (${opts.level}) was not loaded`);
      const geometryId = mesh.addGeometry(geometryFrom(partArrays(buffer, range)));
      const instanceId = mesh.addInstance(geometryId);
      if (colourInstances) mesh.setColorAt(instanceId, new Color(system.colour));
      atlas.parts.set(part.id, {
        record: part,
        system: systemId,
        mesh,
        instanceId,
        geometryIds: new Map([[opts.level, geometryId]]),
        currentLevel: opts.level,
      });
    }
    atlas.meshes.set(systemId, mesh);
    atlas.root.add(mesh);
  }
  return atlas;
}
